import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
    useDisclosure,
} from "@heroui/react";
import { LuPlus, LuPackage, LuPencil } from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import {
    FilterData,
    MethodSelectModal,
    FormulaListModal,
    ConfirmFormulaProductionModal,
} from "./components";
import { useUser } from "@/context/UserContext";
import { formatThousands, formatDate } from "@/utils";

// Initial filter values
const initialFilters = {
    start_date: "",
    end_date: "",
    warehouse_id: "",
    material_id: "",
    production_number: "",
};

const ProductionData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("material-production.edit");
    const navigate = useNavigate();

    // Modal Disclosures
    const methodModal = useDisclosure();
    const formulaModal = useDisclosure();
    const confirmModal = useDisclosure();
    const [selectedFormula, setSelectedFormula] = useState(null);

    const table = useDataTable(
        "material-productions/data",
        null, // No delete endpoint for now
        {},
        initialFilters,
    );

    const { data: warehouses } = useFetchData("select/warehouse");
    const { data: materials } = useFetchData("select/material");

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data. Silakan coba lagi."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "No. Produksi", skeletonWidth: "w-32" },
        { name: "Tanggal", skeletonWidth: "w-24" },
        { name: "Produk Hasil", skeletonWidth: "w-40" },
        { name: "Jumlah", skeletonWidth: "w-20" },
        { name: "Gudang Tujuan", skeletonWidth: "w-32" },
        { name: "Keterangan", skeletonWidth: "w-40" },
        ...(hasAnyAction ? [{ name: "Aksi", skeletonWidth: "w-20", align: "end" }] : []),
    ];

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Riwayat Produksi"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Manufaktur", href: "/productions/data" },
                        { label: "Riwayat" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    {/* Placeholder for Filter Button */}
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("material-production.create") && (
                        <Button
                            onPress={methodModal.onOpen}
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Buat Produksi
                        </Button>
                    )}
                </div>
            </div>

            {/* Modal Step 1: Method Select */}
            <MethodSelectModal
                isOpen={methodModal.isOpen}
                onClose={methodModal.onClose}
                onSelectManual={() => {
                    methodModal.onClose();
                    navigate("/productions/create");
                }}
                onSelectFormula={() => {
                    methodModal.onClose();
                    formulaModal.onOpen();
                }}
            />

            {/* Modal Step 2: Formula List */}
            <FormulaListModal
                isOpen={formulaModal.isOpen}
                onClose={formulaModal.onClose}
                onBack={() => {
                    formulaModal.onClose();
                    methodModal.onOpen();
                }}
                onSelectFormula={(formula) => {
                    setSelectedFormula(formula);
                    formulaModal.onClose();
                    confirmModal.onOpen();
                }}
            />

            {/* Modal Step 3: Confirm Production */}
            <ConfirmFormulaProductionModal
                isOpen={confirmModal.isOpen}
                onClose={() => {
                    confirmModal.onClose();
                    setSelectedFormula(null);
                }}
                onBack={() => {
                    confirmModal.onClose();
                    formulaModal.onOpen();
                }}
                formula={selectedFormula}
                onSuccess={table.refetch}
            />

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={{
                    warehouses: warehouses || [],
                    materials: materials || [],
                }}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Produksi"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada data yang cocok dengan filter."
                                    : "Mulai dengan membuat transaksi produksi baru."
                            }
                            icon={LuPackage}
                        />
                    ) : (
                        <Table
                            aria-label="Tabel Riwayat Produksi"
                            shadow="none"
                        >
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="production_number">
                                    No. Produksi
                                </TableColumn>
                                <TableColumn key="date">Tanggal</TableColumn>
                                <TableColumn key="material">
                                    Produk Hasil
                                </TableColumn>
                                <TableColumn key="quantity">Jumlah</TableColumn>
                                <TableColumn key="warehouse">
                                    Gudang Tujuan
                                </TableColumn>
                                <TableColumn key="description">
                                    Keterangan
                                </TableColumn>
                                {hasAnyAction && (
                                    <TableColumn align="end" key="actions">
                                        Aksi
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {(table.pagination.page - 1) *
                                                    table.pagination.perPage +
                                                    table.items.indexOf(item) +
                                                    1}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="sm"
                                                variant="flat"
                                                color="primary"
                                                className="font-mono text-xs"
                                            >
                                                {item.production_number}
                                            </Chip>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {formatDate(
                                                    item.production_date,
                                                )}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.material?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {formatThousands(item.quantity)}{" "}
                                                {item.unit?.name}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.warehouse?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText
                                                size="xs"
                                                variant="caption"
                                                className="line-clamp-1"
                                            >
                                                {item.description || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        {hasAnyAction && (
                                            <TableCell>
                                                <div className="relative flex items-center justify-end gap-2">
                                                    <Tooltip content="Edit Produksi">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            as={Link}
                                                            to={`/productions/${item.id}/edit`}
                                                        >
                                                            <LuPencil
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default ProductionData;
